import dayjs from 'dayjs';
import { UiButton } from 'znanie-uikit';

import Cover, { getCoverBackgroundSettings } from 'components/common/Cover';
import { Link } from 'components/common/Link';
import { buildImgProxyUrl } from 'components/common/ImageProxy';

import PosterDate from 'components/widget/PosterDate';
import BroadcastStatusWrapper from 'components/widget/BroadcastStatus/BroadcastStatusWrapper';

import { BroadcastType, MainSliderType } from 'graphql/types';

import { useMediaQueries } from 'hooks/utils/useMediaQueries';

import { trackAnalyticsData, AnalyticsEvent } from 'utils/analytics';

import css from './MainSlider.module.scss';

interface IProps {
  data: MainSliderType[];
}

export default function MainSlider({ data }: IProps) {
  const { isMobile } = useMediaQueries();

  const getButtonText = (item: MainSliderType) => {
    if (
      item.events?.length === 0 ||
      (item.events && item.events[0] && item.events[0].isOffline)
    ) {
      return 'Подробнее';
    }
    if (item.events && item.events[0] && item.events[0]?.broadcasts) {
      const currentBroadcast = (
        item.events[0].broadcasts as BroadcastType[]
      ).find(
        (b) =>
          b.published &&
          dayjs().unix() > dayjs(b.startTime).unix() &&
          dayjs().unix() < dayjs(b.finishTime).unix()
      );

      if (currentBroadcast) return 'Смотреть трансляцию';
      return 'Подробнее';
    }
  };

  const handleSlideClick = (item: MainSliderType) => {
    const { id, order, url = '' } = item;

    if (url) {
      trackAnalyticsData(AnalyticsEvent.MAIN_SLIDER_CLICK, {
        id,
        url,
        order,
      });
    }
  };

  return (
    <div className={css.block}>
      <div className={css.inner}>
        <Cover
          size="intro"
          type="slider"
          sliderSettings={{
            dots: true,
            dotsDecoration: true,
            autoplaySpeed: 6000,
          }}
        >
          {data.map((item, i) => {
            const { url, type, poster } = getCoverBackgroundSettings(
              {
                imageUrl: item?.imageUrl || '',
                imageMobileUrl: item?.mobileImage || '',
                videoUrl: item?.videoUrl || '',
              },
              isMobile
            );
            return (
              <Cover.SliderItem
                key={`main_slide_${i}`}
                type={type}
                background={url}
                imgProxyProps={
                  type === 'image'
                    ? {
                        sourceWidth: isMobile ? 675 : 1350,
                        sourceHeight: isMobile ? 570 : 515,
                        resizingType: 'fill',
                      }
                    : undefined
                }
                poster={
                  poster
                    ? buildImgProxyUrl(
                        'fill',
                        isMobile ? 675 : 1350,
                        isMobile ? 570 : 515,
                        0,
                        'no',
                        100,
                        poster
                      )
                    : ''
                }
                widgets={{
                  top: {
                    left:
                      item.events &&
                      item.events[0] &&
                      !item.events[0].isOffline ? (
                        <div className={css.broadcastStatus}>
                          <BroadcastStatusWrapper
                            broadcasts={
                              (item.events[0].broadcasts as BroadcastType[]) ||
                              []
                            }
                            withTime
                          />
                        </div>
                      ) : null,
                    right: item.categories?.map((c) => c?.name).join(','),
                  },
                  bottom: {
                    right: (
                      <PosterDate
                        dateStart={item.date}
                        dateEnd={item.endDate}
                        place={item.venue?.address?.city || ''}
                        event={(item.events && item.events[0]) || null}
                      />
                    ),
                  },
                }}
              >
                <Link
                  href={item.url || ''}
                  target="_blank"
                  onClick={() => handleSlideClick(item)}
                >
                  <Cover.Title as="h1" withFormat>
                    {item.name}
                  </Cover.Title>
                </Link>
                <Cover.Description withFormat>
                  {item.description as string}
                </Cover.Description>
                <Cover.Buttons>
                  <UiButton
                    size="medium"
                    href={item.url || ''}
                    target="_blank"
                    onClick={() => handleSlideClick(item)}
                  >
                    {getButtonText(item)}
                  </UiButton>
                </Cover.Buttons>
              </Cover.SliderItem>
            );
          })}
        </Cover>
      </div>
    </div>
  );
}
